import { HISTORY_ACTION_MODE } from '@candulabs/craft';
import { useEffect, useRef } from 'react';

import { NodeDataWithId } from 'craft/hooks/useNodesData';

interface WithPosition {
  position: number;
}

type SetColumnSize = (offset: number, width: number, historyMode?: HISTORY_ACTION_MODE) => void;

const nodeWidth = (node: any): number => parseInt(node.custom.custom?.width, 10);

/**
 * Listens for missing nodes in ChildrenData and resizes siblings into the space
 * This needs to account for both node deletion (modification required) and undo / redo (no action)
 */
export const useGrowSiblingsIntoDeletedSpace = (
  setColumnSize: SetColumnSize,
  childrenData: NodeDataWithId[],
) => {
  const previousChildrenData = useRef(childrenData);

  useEffect(() => {
    if (!setColumnSize || childrenData.length === 0) {
      return;
    }

    const previousChildren = previousChildrenData.current;

    const missingChildren: Array<NodeDataWithId & WithPosition> = previousChildren
      .map((previousChild: any, idx: number) => {
        if (!childrenData.find((child: any) => previousChild.id === child.id)) {
          return { ...previousChild, position: idx };
        }
        return null;
      })
      .filter(Boolean);

    const missingWidth = childrenData.reduce((acc, child) => acc - nodeWidth(child), 12);
    if (missingChildren.length === 0 || missingWidth === 0) {
      return;
    }

    const { position } = missingChildren[0];
    const siblingToResize = position > 0 ? position - 1 : 0;
    const newWidth = missingWidth + nodeWidth(childrenData[siblingToResize]);
    setColumnSize(siblingToResize, newWidth, HISTORY_ACTION_MODE.MERGE);
  }, [childrenData]);

  useEffect(() => {
    previousChildrenData.current = childrenData;
  }, [childrenData]);

  return {};
};
